import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { CategoryKind, TransactionListQuery } from '@familywallet/shared';
import { api } from './api';

export const accountsKey = ['accounts'] as const;
export const accountKey = (id: string) => ['accounts', 'one', id] as const;
export const balancesKey = ['balances'] as const;
export const categoriesKey = ['categories'] as const;
export const familyKey = ['family'] as const;
export const ratesKey = ['rates'] as const;
export const transactionsKey = ['transactions'] as const;
export const payeesKey = ['payees'] as const;
export const debtsKey = ['debts'] as const;
export const peopleKey = ['people'] as const;
export const budgetsKey = ['budgets'] as const;
export const limitsKey = ['limits'] as const;
export const goalsKey = ['goals'] as const;
export const reportsKey = ['reports'] as const;

export function useAccounts(includeArchived = false) {
  return useQuery({
    queryKey: [...accountsKey, 'list', { includeArchived }],
    queryFn: () => api.accounts.list({ includeArchived }),
  });
}

export function useAccount(id: string) {
  return useQuery({ queryKey: accountKey(id), queryFn: () => api.accounts.get(id) });
}

export function useBalanceSummary() {
  return useQuery({ queryKey: balancesKey, queryFn: () => api.balances.summary() });
}

export function useCategories(kind: CategoryKind, includeArchived: boolean) {
  return useQuery({
    queryKey: [...categoriesKey, kind, { includeArchived }],
    queryFn: () =>
      api.categories.list({ kind, includeArchived: includeArchived ? 'true' : 'false' }),
  });
}

export function useFamily() {
  return useQuery({ queryKey: familyKey, queryFn: () => api.family.get() });
}

/** Today's family rate per currency (1 base = X), with the "older than 7 days" flag. */
export function useCurrentRates() {
  return useQuery({ queryKey: [...ratesKey, 'current'], queryFn: () => api.rates.current() });
}

export function useRateHistory() {
  return useInfiniteQuery({
    queryKey: [...ratesKey, 'history'],
    queryFn: ({ pageParam }) => api.rates.list({ cursor: pageParam, limit: '30' }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** Today's family rate per currency as a map (1 base = X), for estimates and forms. */
export function latestRateMap(
  data: { items: { quoteCurrency: string; current: { rate: string } | null }[] } | undefined,
): Record<string, string> {
  return Object.fromEntries(
    (data?.items ?? []).flatMap((i) => (i.current ? [[i.quoteCurrency, i.current.rate]] : [])),
  );
}

/** Activity filters, as in the list query (without paging). */
export type TransactionFilters = Omit<TransactionListQuery, 'cursor' | 'limit'>;

/** Transactions the viewer can see, newest first, a page at a time, optionally filtered. */
export function useTransactions(pageSize = 30, filters: TransactionFilters = {}) {
  return useInfiniteQuery({
    queryKey: [...transactionsKey, 'list', pageSize, filters],
    queryFn: ({ pageParam }) =>
      api.transactions.list({ ...filters, cursor: pageParam, limit: `${pageSize}` }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** The last account per kind and currency per account the viewer used (Add screen defaults). */
export function useTransactionDefaults() {
  return useQuery({
    queryKey: [...transactionsKey, 'defaults'],
    queryFn: () => api.transactions.defaults(),
  });
}

/** Payee suggestions for what was typed, most used first. */
export function usePayees(q: string) {
  return useQuery({
    queryKey: [...payeesKey, q],
    queryFn: () => api.payees.list({ q: q || undefined, limit: '6' }),
    placeholderData: (previous) => previous,
  });
}

/** Every payee the viewer may see (Payees screen), most used first; archived ones too. */
export function usePayeeList(q: string) {
  return useQuery({
    queryKey: [...payeesKey, 'all', q],
    queryFn: () => api.payees.list({ q: q || undefined, limit: '100', includeArchived: 'true' }),
    placeholderData: (previous) => previous,
  });
}

/** One transaction (Activity detail). */
export function useTransaction(id: string) {
  return useQuery({
    queryKey: [...transactionsKey, 'one', id],
    queryFn: () => api.transactions.get(id),
    enabled: id !== '',
  });
}

/** Every visible debt with totals per currency (outside people only). */
export function useDebts() {
  return useQuery({ queryKey: debtsKey, queryFn: () => api.debts.list() });
}

export function useDebt(id: string) {
  return useQuery({
    queryKey: [...debtsKey, 'one', id],
    queryFn: () => api.debts.get(id),
    enabled: id !== '',
  });
}

/** People, most used first; `q` narrows by name. */
export function usePeople(q = '', includeArchived = false) {
  return useQuery({
    queryKey: [...peopleKey, 'list', { q, includeArchived }],
    queryFn: () =>
      api.people.list({
        ...(q ? { q } : {}),
        includeArchived: includeArchived ? 'true' : 'false',
      }),
  });
}

export function usePerson(id: string) {
  return useQuery({ queryKey: [...peopleKey, 'one', id], queryFn: () => api.people.get(id) });
}

export const recurringKey = ['recurring'] as const;

/** Recurring templates the viewer may record, with their due occurrences. */
export function useRecurring(enabled = true) {
  return useQuery({ queryKey: recurringKey, queryFn: () => api.recurring.list(), enabled });
}

/** A month's budgets (default: this month); only for viewers with budgets.view. */
export function useBudgets(month?: string, enabled = true) {
  return useQuery({
    queryKey: [...budgetsKey, month ?? 'current'],
    queryFn: () => api.budgets.month(month),
    enabled,
  });
}

/** Children's monthly limits: every child for parents, their own for a child. */
export function useLimits() {
  return useQuery({ queryKey: limitsKey, queryFn: () => api.limits.list() });
}

export function useGoals(includeArchived = false) {
  return useQuery({
    queryKey: [...goalsKey, 'list', { includeArchived }],
    queryFn: () => api.goals.list(includeArchived),
  });
}

export function useGoal(id: string, enabled = true) {
  return useQuery({
    queryKey: [...goalsKey, 'one', id],
    queryFn: () => api.goals.get(id),
    enabled,
  });
}
